import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Alert,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { BulkUploadConfig } from '../types';
import { config } from '../config';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface BulkUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  config: BulkUploadConfig;
  clientId?: number;
}

interface BulkUploadResponse {
  message: string;
  created_count: number;
  skipped_count: number;
  created_products?: string[];
  skipped_products?: string[];
  created_clients?: string[];
  skipped_clients?: string[];
}

export default function BulkUpload({ open, onClose, onSuccess, config: uploadConfig, clientId }: BulkUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const downloadTemplate = () => {
    const template = uploadConfig.templateColumns.join(',') + '\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = uploadConfig.templateName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);
    if (clientId) {
      formData.append('client_id', clientId.toString());
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}${uploadConfig.endpoint}`, {
        method: 'POST',
        body: formData,
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('Upload error:', responseData);
        throw new Error(responseData.detail?.message || 'Upload failed');
      }

      const result: BulkUploadResponse = responseData;
      console.log('Upload result:', result);
      setUploadResult(result);
      if (result.created_count > 0) {
        onSuccess();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{uploadConfig.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Upload a CSV file containing data. The file should include the following columns:
          </Typography>
          <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {uploadConfig.requiredColumns.map((col: string) => (
              <React.Fragment key={col}>
                • {col} (required)<br />
              </React.Fragment>
            ))}
            {uploadConfig.optionalColumns.map((col: string) => (
              <React.Fragment key={col}>
                • {col} (optional)<br />
              </React.Fragment>
            ))}
          </Typography>
          <Button
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            variant="outlined"
            size="small"
          >
            Download Template
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            Select CSV File
            <VisuallyHiddenInput type="file" accept=".csv" onChange={handleFileChange} />
          </Button>

          {file && (
            <Typography variant="body2" color="text.secondary">
              Selected file: {file.name}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          {uploadResult && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadResult(null)}>
              {uploadResult.message}
              <div>Created: {uploadResult.created_count} items</div>
              <div>Skipped: {uploadResult.skipped_count} items</div>
              {uploadResult.created_products && uploadResult.created_products.length > 0 && (
                <div>Created products: {uploadResult.created_products.join(', ')}</div>
              )}
              {uploadResult.skipped_products && uploadResult.skipped_products.length > 0 && (
                <div>Skipped products: {uploadResult.skipped_products.join(', ')}</div>
              )}
              {uploadResult.created_clients && uploadResult.created_clients.length > 0 && (
                <div>Created clients: {uploadResult.created_clients.join(', ')}</div>
              )}
              {uploadResult.skipped_clients && uploadResult.skipped_clients.length > 0 && (
                <div>Skipped clients: {uploadResult.skipped_clients.join(', ')}</div>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={!file || uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : undefined}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
